import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { showToast } from '../utils/ShowToast';
import Loader from '../utils/Loader';
import { FaCalendarAlt, FaPlus, FaTimes } from 'react-icons/fa';

const AvailabilityModal = ({ isOpen, onClose }) => {
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) fetchUnavailableDates();
  }, [isOpen]);

  const fetchUnavailableDates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/sellers/seller/current`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const sellerId = res.data.seller?._id;
      if (!sellerId) {
        setLoading(false);
        return;
      }
      const availRes = await axios.get(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/sellers/seller/${sellerId}/unavailable-dates`
      );
      setUnavailableDates(availRes.data.unavailableDates || []);
    } catch (err) {
      showToast('Failed to load availability', 'error');
      setUnavailableDates([]);
    } finally {
      setLoading(false);
    }
  };

  const addDate = () => {
    if (!selectedDate) return;
    if (unavailableDates.includes(selectedDate)) {
      showToast('Date already added', 'info');
      return;
    }
    setUnavailableDates((prev) => [...prev, selectedDate].sort());
    setSelectedDate('');
  };

  const addRange = () => {
    if (!rangeFrom || !rangeTo) {
      showToast('Please select both start and end dates', 'info');
      return;
    }
    const from = new Date(rangeFrom + 'T12:00:00');
    const to = new Date(rangeTo + 'T12:00:00');
    if (to < from) {
      showToast('End date must be on or after start date', 'error');
      return;
    }
    const newDates = [];
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      const str = d.toISOString().split('T')[0];
      if (!unavailableDates.includes(str)) newDates.push(str);
    }
    if (newDates.length === 0) {
      showToast('All dates in range are already added', 'info');
      return;
    }
    setUnavailableDates((prev) => [...prev, ...newDates].sort());
    setRangeFrom('');
    setRangeTo('');
    showToast(`Added ${newDates.length} date(s)`, 'success');
  };

  const removeDate = (date) => {
    setUnavailableDates((prev) => prev.filter((d) => d !== date));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/sellers/seller/unavailable-dates`,
        { unavailableDates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Availability updated successfully', 'success');
      onClose();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  if (!isOpen) return null;

  return (
    <div className="availabilityModalOverlay" onClick={onClose}>
      <div className="availabilityModal" onClick={(e) => e.stopPropagation()}>
        <div className="availabilityModalHeader">
          <h2><FaCalendarAlt /> Set Availability</h2>
          <button type="button" className="availabilityModalClose" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <div className="availabilityModalBody">
          <p className="availabilityModalHint">
            Mark dates when you are fully booked and cannot accept new orders. Buyers will not be able to select these dates for delivery or pickup.
          </p>
          {loading ? (
            <div className="availabilityModalLoaderWrap"><Loader type="simpleMini" /></div>
          ) : (
            <>
              <div className="availabilityAddSection">
                <div className="availabilityAddRow">
                  <label className="availabilityAddLabel">Single date</label>
                  <div className="availabilityDateAdd">
                    <input
                      type="date"
                      className="inputField availabilityDateInput"
                      value={selectedDate}
                      min={today}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      title="Click to open date picker"
                    />
                    <button type="button" className="primaryBtn availabilityAddBtn" onClick={addDate} disabled={!selectedDate}>
                      Add
                    </button>
                  </div>
                </div>
                <div className="availabilityAddRow">
                  <label className="availabilityAddLabel">Date range</label>
                  <div className="availabilityRangeAdd">
                    <input
                      type="date"
                      className="inputField availabilityDateInput"
                      value={rangeFrom}
                      min={today}
                      onChange={(e) => setRangeFrom(e.target.value)}
                      placeholder="From"
                    />
                    <span className="availabilityRangeSep">to</span>
                    <input
                      type="date"
                      className="inputField availabilityDateInput"
                      value={rangeTo}
                      min={rangeFrom || today}
                      onChange={(e) => setRangeTo(e.target.value)}
                      placeholder="To"
                    />
                    <button type="button" className="primaryBtn availabilityAddBtn" onClick={addRange} disabled={!rangeFrom || !rangeTo}>
                      Add Range
                    </button>
                  </div>
                </div>
              </div>
              <div className="availabilityDateList">
                <h4>Unavailable dates ({unavailableDates.length})</h4>
                {unavailableDates.length === 0 ? (
                  <p className="availabilityEmpty">No dates marked. Add dates above when you are not available.</p>
                ) : (
                  <ul>
                    {unavailableDates.map((d) => (
                      <li key={d}>
                        <span>{new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <button type="button" className="availabilityRemoveBtn" onClick={() => removeDate(d)}>Remove</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
        <div className="availabilityModalFooter">
          <button type="button" className="modalButton cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="primaryBtn" onClick={handleSave} disabled={loading || saving}>
            {saving ? <Loader type="miniPlus" /> : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityModal;
